import { CircleOutlined, LocationOn, Polyline } from '@mui/icons-material';
import { getContrastRatio, Stack } from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import NewFieldMap from '/@/components/map/new-field-map';
import { useCoordinatesArea } from '/@/hooks/geometry/area';
import { _ } from '/@/locale';
import MuiFab from '/@/mui/fab';
import { getSessionCountry } from '/@/store/session/selectors';
import type { TEditOption, TNewField } from '..';

const DrawFieldV2 = ({
    newField,
    setNewField,
    editMode,
    setEditMode,
    fields = [],
}: {
    newField: TNewField | undefined;
    setNewField: React.Dispatch<React.SetStateAction<TNewField | undefined>>;
    editMode: TEditOption | undefined;
    setEditMode: React.Dispatch<React.SetStateAction<TEditOption | undefined>>;
    fields?: any[];
}) => {
    const editMapRef = useRef<null>(null);
    const country = useSelector(getSessionCountry);

    const { area } = useCoordinatesArea(newField?.geometry || []);
    useMemo(() => {
        setNewField((f) => ({ ...f, area }));
    }, [area]);

    // Center Map/Field
    useEffect(() => {
        if (!editMapRef.current) return;
        if (fields.length) {
            const bounds = new google.maps.LatLngBounds();
            fields.map(({ lat, lng }) => {
                bounds.extend(new google.maps.LatLng(lat, lng));
            });
            editMapRef.current.fitBounds(bounds);
            setTimeout(
                ({ editMapRef }) => {
                    if (!editMapRef.current) return;
                    if (editMapRef.current.getZoom() > 16) editMapRef.current.setZoom(16);
                },
                100,
                { editMapRef }
            );
        } else if (country) {
            const googleBounds = new google.maps.LatLngBounds(
                new google.maps.LatLng(country.bounds.south, country.bounds.west), // Southwest corner
                new google.maps.LatLng(country.bounds.north, country.bounds.east) // Northeast corner
            );
            editMapRef.current.fitBounds(googleBounds);
        }
    }, [country, fields]);

    return (
        <>
            <NewFieldMap
                ref={editMapRef}
                disabled={false}
                mode={editMode}
                field={newField || {}}
                setField={setNewField}
                country={country.code}
                fields={fields}
            />
            {editMode === undefined && (
                <Stack
                    spacing={2}
                    sx={{
                        position: 'absolute',
                        bottom: '5%',
                        right: '10px',
                    }}
                >
                    <MuiFab
                        Icon={<LocationOn />}
                        TooltipProps={{
                            title: _('Marcar un punto'),
                            placement: 'left',
                        }}
                        FabProps={{
                            'aria-label': 'add-point',
                            onClick: () => setEditMode('point'),
                            size: 'medium',
                            sx: {
                                '&:hover': {
                                    transform: 'scale(1.1)',
                                },
                            },
                        }}
                    />
                    <MuiFab
                        Icon={<Polyline />}
                        TooltipProps={{
                            title: _('Dibujar un polígono'),
                            placement: 'left',
                        }}
                        FabProps={{
                            'aria-label': 'add-point',
                            onClick: () => setEditMode('polygon'),
                            size: 'medium',
                            sx: {
                                '&:hover': {
                                    transform: 'scale(1.1)',
                                },
                            },
                        }}
                    />
                    <MuiFab
                        Icon={<CircleOutlined />}
                        TooltipProps={{
                            title: _('Dibujar un círculo'),
                            placement: 'left',
                            sx: {
                                background: (t) => t.palette.primary.main,
                                color: (t) =>
                                    getContrastRatio(t.palette.primary.main, '#ffffff') > 4.5 ? '#ffffff' : '#000000',
                                '& .MuiTooltip-tooltip': {
                                    background: (t) => t.palette.primary.main,
                                    color: (t) =>
                                        getContrastRatio(t.palette.primary.main, '#ffffff') > 4.5
                                            ? '#ffffff'
                                            : '#000000',
                                },
                            },
                        }}
                        FabProps={{
                            'aria-label': 'draw-circle',
                            onClick: () => setEditMode('circle'),
                            size: 'medium',
                            sx: {
                                '&:hover': {
                                    transform: 'scale(1.1)',
                                },
                            },
                        }}
                    />
                </Stack>
            )}
        </>
    );
};

export default DrawFieldV2;
